import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const scrollToHashTarget = (hash) => {
  const id = hash.replace(/^#/, '');
  if (!id) return;

  const rafId = requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  return () => cancelAnimationFrame(rafId);
};

/**
 * On route change: scroll to top, then if location has a hash scroll to that element.
 * Ensures new pages (e.g. /privacy) start at the top; hash links (e.g. /#features) still scroll to the section.
 */
export function useScrollToHash() {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (!hash) return;
    return scrollToHashTarget(hash);
  }, [pathname, hash]);

  useEffect(() => {
    const handleHashLinkClick = (event) => {
      if (!(event.target instanceof Element)) return;

      const link = event.target.closest('a[href*="#"]');
      if (!link) return;

      const url = new URL(link.getAttribute('href'), window.location.href);
      const isSamePage =
        url.origin === window.location.origin &&
        url.pathname === window.location.pathname &&
        url.hash;

      if (isSamePage) {
        scrollToHashTarget(url.hash);
      }
    };

    document.addEventListener('click', handleHashLinkClick);
    return () => document.removeEventListener('click', handleHashLinkClick);
  }, []);
}
